import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Instagram, Youtube, Twitter, CheckCircle, 
  Clock, XCircle, Link2, Copy, ExternalLink, Loader2, Shield,
  AlertCircle, RefreshCw
} from 'lucide-react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png";

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const platformConfig = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'from-pink-500 to-purple-500',
    placeholder: '@username or https://instagram.com/username',
    urlPattern: /instagram\.com\/([^\/\?]+)/,
  },
  tiktok: {
    name: 'TikTok',
    icon: TikTokIcon,
    color: 'from-black to-gray-800',
    placeholder: '@username or https://tiktok.com/@username',
    urlPattern: /tiktok\.com\/@?([^\/\?]+)/,
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: 'from-red-500 to-red-600',
    placeholder: '@handle or https://youtube.com/@handle',
    urlPattern: /youtube\.com\/@?([^\/\?]+)/,
  },
  twitter: {
    name: 'Twitter/X',
    icon: Twitter,
    color: 'from-blue-400 to-blue-500',
    placeholder: '@username or https://twitter.com/username',
    urlPattern: /twitter\.com\/([^\/\?]+)|x\.com\/([^\/\?]+)/,
  },
};

const statusConfig = {
  pending: { color: 'bg-yellow-500/20 text-yellow-600', icon: Clock, label: 'Pending Review' },
  verified: { color: 'bg-green-500/20 text-green-600', icon: CheckCircle, label: 'Verified' },
  rejected: { color: 'bg-red-500/20 text-red-600', icon: XCircle, label: 'Rejected' },
  unlinked: { color: 'bg-gray-500/20 text-gray-600', icon: Link2, label: 'Not Linked' },
};

// Social Link Card Component
const SocialLinkCard = ({ platform, data, onLink, onUnlink, verificationCode }) => {
  const config = platformConfig[platform];
  const Icon = config.icon;
  const status = data?.status || 'unlinked';
  const statusInfo = statusConfig[status];
  const StatusIcon = statusInfo.icon;

  return (
    <Card className="overflow-hidden">
      <div className={`h-2 bg-gradient-to-r ${config.color}`} />
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">{config.name}</h3>
              {data?.handle && (
                <p className="text-sm text-muted-foreground">@{data.handle}</p>
              )}
            </div>
          </div>
          <Badge className={statusInfo.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>

        {status === 'verified' && data?.stats && (
          <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-center">
              <p className="text-lg font-bold">{(data.stats.followers / 1000).toFixed(1)}K</p>
              <p className="text-xs text-muted-foreground">Followers</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold">{data.stats.engagement}%</p>
              <p className="text-xs text-muted-foreground">Engagement</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold">{data.stats.posts}</p>
              <p className="text-xs text-muted-foreground">Posts</p>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Verification Pending</p>
                <p className="text-xs text-yellow-600 mt-1">
                  Add this code to your bio: <code className="bg-yellow-100 px-1 rounded">{verificationCode}</code>
                </p>
              </div>
            </div>
          </div>
        )}

        {status === 'rejected' && data?.rejection_reason && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{data.rejection_reason}</p>
          </div>
        )}

        <div className="flex gap-2">
          {status === 'unlinked' ? (
            <Button onClick={() => onLink(platform)} className="flex-1">
              <Link2 className="w-4 h-4 mr-2" />
              Link Account
            </Button>
          ) : (
            <>
              {data?.profile_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={data.profile_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View
                  </a>
                </Button>
              )}
              {status === 'pending' && (
                <Button variant="outline" size="sm" onClick={() => onLink(platform)}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Update
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onUnlink(platform)} className="text-red-500 hover:text-red-600">
                Unlink
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Verification = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [socialLinks, setSocialLinks] = useState({});
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [profileUrl, setProfileUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Generate verification code from user ID
  const verificationCode = `cmdout-${user?.user_id?.slice(-8) || 'verify'}`;

  const fetchSocialLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/social-links');
      setSocialLinks(response.data.social_links || {});
    } catch (error) {
      // Use data from user profile if endpoint doesn't exist
      const profile = user?.creator_profile || {};
      const links = {};
      if (profile.instagram_handle) {
        links.instagram = { handle: profile.instagram_handle, status: 'pending' };
      }
      if (profile.tiktok_handle) {
        links.tiktok = { handle: profile.tiktok_handle, status: 'pending' };
      }
      if (profile.youtube_handle) {
        links.youtube = { handle: profile.youtube_handle, status: 'pending' };
      }
      setSocialLinks(links);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSocialLinks();
  }, [fetchSocialLinks]);

  const handleOpenLinkDialog = (platform) => {
    setSelectedPlatform(platform);
    setProfileUrl(socialLinks[platform]?.profile_url || '');
    setShowLinkDialog(true);
  };

  const handleLinkAccount = async () => {
    if (!profileUrl.trim()) {
      toast.error('Please enter your profile URL or username');
      return;
    }

    setSubmitting(true);
    try {
      // Extract username from URL or use as-is
      let handle = profileUrl.trim();
      const config = platformConfig[selectedPlatform];
      const match = handle.match(config.urlPattern);
      if (match) {
        handle = match[1] || match[2];
      }
      handle = handle.replace('@', '');

      // Build profile URL
      let fullUrl = profileUrl;
      if (!profileUrl.includes('http')) {
        const urlMap = {
          instagram: `https://instagram.com/${handle}`,
          tiktok: `https://tiktok.com/@${handle}`,
          youtube: `https://youtube.com/@${handle}`,
          twitter: `https://twitter.com/${handle}`,
        };
        fullUrl = urlMap[selectedPlatform];
      }

      // Save to backend
      await api.post('/users/social-links', {
        platform: selectedPlatform,
        handle: handle,
        profile_url: fullUrl,
      });

      toast.success(`${platformConfig[selectedPlatform].name} linked! Verification pending.`);
      setShowLinkDialog(false);
      fetchSocialLinks();
    } catch (error) {
      toast.error('Failed to link account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlinkAccount = async (platform) => {
    try {
      await api.delete(`/users/social-links/${platform}`);
      toast.success(`${platformConfig[platform].name} unlinked`);
      fetchSocialLinks();
    } catch (error) {
      toast.error('Failed to unlink account');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
      </div>
    );
  }

  const verifiedCount = Object.values(socialLinks).filter(l => l?.status === 'verified').length;
  const linkedCount = Object.keys(socialLinks).length;
  const isProfileVerified = user?.creator_profile?.verified;

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="creator-orb creator-orb-pink" style={{ top: '-10%', right: '5%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-teal" style={{ bottom: '10%', left: '-5%', opacity: 0.25 }} />
        <div className="network-lines opacity-20" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="glass-nav sticky top-0 z-40 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="CmdOut" className="w-8 h-8" />
                <h1 className="text-xl font-bold">Social Verification</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isProfileVerified ? (
                <Badge className="bg-green-500/20 text-green-600">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Profile Verified
                </Badge>
              ) : (
                <Badge className="bg-[#3AAFB9]/20 text-[#3AAFB9]">
                  <Shield className="w-3 h-3 mr-1" />
                  {verifiedCount}/{linkedCount} Verified
                </Badge>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8">
          {/* Verified Profile Banner */}
          {isProfileVerified && (
            <Card className="mb-8 border-green-500/30 bg-green-500/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-green-600">You're Verified!</h2>
                    <p className="text-muted-foreground">Your profile displays a verified badge visible to all brands.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          <Card className="mb-8 border-[#3AAFB9]/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#3AAFB9]" />
                Why Verify Your Social Accounts?
              </CardTitle>
              <CardDescription>
                Verified accounts get more visibility and brand trust
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Build Trust</p>
                    <p className="text-xs text-muted-foreground">Brands prefer verified creators</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Auto-Sync Stats</p>
                    <p className="text-xs text-muted-foreground">Your analytics stay up-to-date</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Verified Badge</p>
                    <p className="text-xs text-muted-foreground">Stand out in search results</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verification Steps */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>How to Verify</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 p-4 border rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#3AAFB9] text-white flex items-center justify-center font-bold mb-2">1</div>
                  <p className="font-medium">Link Your Account</p>
                  <p className="text-sm text-muted-foreground">Enter your social media username or profile URL</p>
                </div>
                <div className="flex-1 p-4 border rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#E8795A] text-white flex items-center justify-center font-bold mb-2">2</div>
                  <p className="font-medium">Add Verification Code</p>
                  <p className="text-sm text-muted-foreground">Add <code className="bg-muted px-1 rounded">{verificationCode}</code> to your bio</p>
                </div>
                <div className="flex-1 p-4 border rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#F472B6] text-white flex items-center justify-center font-bold mb-2">3</div>
                  <p className="font-medium">Get Verified</p>
                  <p className="text-sm text-muted-foreground">Our team will verify within 24-48 hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Platform Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {Object.keys(platformConfig).map(platform => (
              <SocialLinkCard
                key={platform}
                platform={platform}
                data={socialLinks[platform]}
                onLink={handleOpenLinkDialog}
                onUnlink={handleUnlinkAccount}
                verificationCode={verificationCode}
              />
            ))}
          </div>
        </main>
      </div>

      {/* Link Account Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPlatform && (
                <>
                  {React.createElement(platformConfig[selectedPlatform].icon, { className: 'w-5 h-5' })}
                  Link {platformConfig[selectedPlatform]?.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Profile URL or Username</Label>
              <Input
                placeholder={selectedPlatform ? platformConfig[selectedPlatform].placeholder : ''}
                value={profileUrl}
                onChange={(e) => setProfileUrl(e.target.value)}
              />
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-1">Your Verification Code</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-background rounded border text-sm">{verificationCode}</code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(verificationCode);
                    toast.success('Code copied!');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Add this code to your bio for verification
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button onClick={handleLinkAccount} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Link Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Verification;
